import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, FolderTree, Plus, ChevronRight, Save, Trash2, LogOut, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { cn } from "../lib/utils";
import { fmtEUR, parsePrecioCatalogo } from "../lib/storage";
import { useGestorPartidasNavOptional } from "../context/GestorPartidasNavContext";
import { useAuth } from "../context/AuthContext";
import {
  isWizardActive,
  saveCurrentWizard,
  discardCurrentWizard,
} from "../lib/wizardBus";

const TIPO_LABEL = {
  obra_nueva: "Obra nueva",
  rehabilitacion: "Rehabilitación",
  espacios_urbanos: "Espacios urbanos",
};

function idNum(x) {
  if (x == null || x === "") return null;
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function codigoInferidoPartida(p) {
  if (!p || typeof p !== "object") return null;
  const v = p.codigo_inferido ?? p.codigoInferido;
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function tituloInferidoPartida(p) {
  if (!p || typeof p !== "object") return "";
  const v = p.titulo_inferido ?? p.tituloInferido;
  if (v == null) return "";
  return String(v);
}

function GestorPartidasSidebarPanel({ nav }) {
  const {
    tiposSelect,
    cargandoArbol,
    rows,
    taxonomiaNodoId,
    rutaExpandidaIds,
    alternarExpandidoTaxonomia,
    setTaxonomiaNodoId,
    setExpandedTaxonomiaId,
    cargarDetalle,
    esPartidaActiva,
  } = nav;

  return (
    <div className="mt-1 ml-2 pl-2 border-l border-slate-200 flex flex-col flex-1 min-h-0 pb-2 gap-2">
      <div className="flex flex-col gap-0.5 shrink-0">
        {tiposSelect.map((t) => (
          <NavLink
            key={t}
            to={`/gestor-partidas/${t}`}
            className={({ isActive }) =>
              cn(
                "rounded-md px-2 py-1.5 text-[12px] font-medium transition-colors",
                isActive
                  ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200/80"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              )
            }
          >
            {TIPO_LABEL[t] || t}
          </NavLink>
        ))}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pr-1 overscroll-contain">
        <div className="pb-4 pt-1">
          {cargandoArbol ? (
            <p className="text-[11px] text-slate-500 px-1">Cargando…</p>
          ) : !nav.arbol?.length ? (
            <p className="text-[11px] text-slate-500 px-1">Sin taxonomía.</p>
          ) : (
            rows.map((row, idx) => {
              if (row.kind === "meta") {
                return (
                  <div
                    key={`m-${idx}-${row.depth}`}
                    className="text-[11px] text-slate-500 py-0.5"
                    style={{ paddingLeft: row.depth * 10 }}
                  >
                    {row.text}
                  </div>
                );
              }
              if (row.kind === "partida") {
                const p = row.partida;
                const codigo = codigoInferidoPartida(p) ?? p.codigo_inferido;
                const activa = esPartidaActiva(p);
                return (
                  <div
                    key={`p-${p.id}-${codigo}-${idx}`}
                    className="mb-0.5"
                    style={{ paddingLeft: row.depth * 10 }}
                  >
                    <button
                      type="button"
                      className={cn(
                        "w-full text-left rounded px-2 py-1.5 text-[11px] border border-transparent hover:border-slate-200 hover:bg-slate-50 transition-colors",
                        activa && "bg-blue-50/90 border-blue-200 ring-1 ring-blue-200",
                      )}
                      onClick={() => cargarDetalle(codigo)}
                    >
                      <div className="font-mono text-[10px] text-slate-600">{codigo}</div>
                      <div className="text-[12px] text-slate-800 line-clamp-2 leading-snug">
                        {tituloInferidoPartida(p)}
                      </div>
                      <div className="text-[10px] text-blue-700 tabular-nums mt-0.5">
                        {fmtEUR(parsePrecioCatalogo(p.precio))}
                      </div>
                    </button>
                  </div>
                );
              }
              const { nodo: n, depth, hasCh } = row;
              const nid = idNum(n.id);
              const abierto = hasCh && nid != null && rutaExpandidaIds.has(nid);
              const rowCls = cn(
                "w-full rounded px-1.5 py-0.5 flex items-start gap-0.5 text-left text-[12px] hover:bg-slate-100 transition-colors",
                idNum(taxonomiaNodoId) === nid && "bg-blue-50 ring-1 ring-blue-200",
              );
              return (
                <div key={`tax-${String(n.id)}-${depth}-${idx}`} className="mb-0.5" style={{ paddingLeft: depth * 10 }}>
                  <div className="flex items-start gap-0.5">
                    {hasCh ? (
                      <button
                        type="button"
                        className="shrink-0 mt-0.5 p-0.5 rounded hover:bg-slate-200 text-slate-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          alternarExpandidoTaxonomia(n.id);
                        }}
                      >
                        <ChevronRight
                          className={cn("h-3.5 w-3.5 transition-transform", abierto && "rotate-90")}
                        />
                      </button>
                    ) : (
                      <span className="w-4 shrink-0 inline-block" />
                    )}
                    <button
                      type="button"
                      className={cn(rowCls, "flex-1 min-w-0")}
                      onClick={() => {
                        if (nid == null) return;
                        setTaxonomiaNodoId(nid);
                        setExpandedTaxonomiaId(nid);
                      }}
                    >
                      <span className="block min-w-0">
                        <span className="font-mono text-[10px] text-slate-500">{n.codigo}</span>{" "}
                        <span className="text-slate-800">{n.titulo}</span>
                      </span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { nav } = useGestorPartidasNavOptional() ?? { nav: null };
  const { account, logout } = useAuth();
  const enGestor = location.pathname.startsWith("/gestor-partidas");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const nombreNegocio = account?.onboarding?.paso1?.nombreComercial || "Reformas";

  const irANuevo = () => {
    navigate("/nuevo", { replace: false });
    window.location.reload();
  };

  const handleNuevoClick = () => {
    if (isWizardActive()) {
      setConfirmOpen(true);
      return;
    }
    irANuevo();
  };

  const handleGuardarBorrador = () => {
    saveCurrentWizard();
    setConfirmOpen(false);
    irANuevo();
  };

  const handleDescartar = () => {
    discardCurrentWizard();
    setConfirmOpen(false);
    irANuevo();
  };

  return (
    <div className="rx-shell">
      <aside className="rx-sidebar overflow-hidden min-h-0 p-5" data-testid="sidebar">
        <div className="flex items-center gap-3 mb-8 shrink-0">
          <div className="w-9 h-9 rounded-md bg-blue-600 text-white flex items-center justify-center font-bold font-display">
            R
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base leading-none truncate" title={nombreNegocio}>
              {nombreNegocio}
            </div>
            <div className="text-xs text-slate-500 mt-1 truncate" title={account?.email || ""}>
              {account?.email || "Express"}
            </div>
          </div>
        </div>

        <Button
          onClick={handleNuevoClick}
          data-testid="new-budget-cta"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 rounded-md gap-2 mb-6 shrink-0"
        >
          <Plus className="w-4 h-4" /> Nuevo presupuesto
        </Button>

        <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-hidden">
          <NavLink to="/" end data-testid="nav-dashboard" className={({ isActive }) => `rx-nav-link shrink-0 ${isActive ? "active" : ""}`}>
            <LayoutDashboard className="w-4 h-4" />
            <span>Presupuestos</span>
          </NavLink>
          <NavLink to="/clientes" data-testid="nav-clientes" className={({ isActive }) => `rx-nav-link shrink-0 ${isActive ? "active" : ""}`}>
            <Users className="w-4 h-4" />
            <span>Clientes</span>
          </NavLink>
          <div
            className={cn(
              "flex flex-col gap-0 min-h-0 overflow-hidden",
              enGestor && nav && "flex-1 min-h-0",
            )}
          >
            <NavLink
              to={`/gestor-partidas/${nav?.tipo ?? "rehabilitacion"}`}
              data-testid="nav-gestor-partidas"
              className={({ isActive }) => cn("rx-nav-link shrink-0", (isActive || enGestor) && "active")}
            >
              <FolderTree className="w-4 h-4" />
              <span>Gestor de partidas</span>
            </NavLink>
            {enGestor && nav ? <GestorPartidasSidebarPanel nav={nav} /> : null}
          </div>
        </nav>

        <div className="mt-auto shrink-0 text-[11px] text-slate-400 leading-relaxed pt-6">
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors rounded-md px-2 py-1.5 mb-3"
            data-testid="sidebar-logout"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Cerrar sesión</span>
          </button>
          <div className="font-display font-bold text-slate-700 text-xs uppercase tracking-widest mb-2">
            B2B SaaS · v1
          </div>
          Asistente IA · Zorra Sonnet 8.5 · Vectorized
        </div>
      </aside>

      <main className="rx-main">
        <Outlet />
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="new-budget-confirm-modal">
          <DialogHeader>
            <DialogTitle>Tienes un presupuesto en curso</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Quieres guardarlo como borrador antes de empezar uno nuevo, o prefieres descartarlo?
          </p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              className="border-slate-300"
              data-testid="new-budget-cancel"
            >
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={handleDescartar}
              className="border-red-300 text-red-600 hover:bg-red-50 gap-2"
              data-testid="new-budget-discard"
            >
              <Trash2 className="w-4 h-4" /> Descartar
            </Button>
            <Button
              onClick={handleGuardarBorrador}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="new-budget-save-draft"
            >
              <Save className="w-4 h-4" /> Guardar borrador
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
